'use client';

import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
    Box,
    Button,
    Container,
    Grid,
    IconButton,
    Link,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import parse from 'html-react-parser';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReturnBack from '../../components/reusable/accordions/ReturnBack';
import SEO from '../../components/reusable/SEO';
import { useCourses } from '../../context/CourseContext';
import VideoPlayer from './VideoPlayer'; // Assuming you have this component
import { config } from '../../config/config';
import { UserSettingsContext } from '../../context/UserSettingsContext';

function WelcomePage() {
    const { courseId } = useParams();
    const { courses, topics, updateCourseProgress } = useCourses();
    const navigate = useNavigate();

    const [course, setCourse] = useState(null);
    const [courseProgress, setCourseProgress] = useState(0);
    const [welcomeCompleted, setWelcomeCompleted] = useState(false);
    const { userSettings } = useContext(UserSettingsContext);

    // Find the course
    useEffect(() => {
        const foundCourse = courses.find((c) => c.slug === courseId);
        if (!foundCourse) {
            navigate('/webinars/lms');
            return;
        }
        setCourse(foundCourse);

        // Calculate course progress
        const completedLessons = topics?.filter((l) => l.is_completed).length;
        const progress =
            topics.length > 0 ? (completedLessons / topics.length) * 100 : 0;
        setCourseProgress(progress);

        // Check if welcome is already completed
        const isWelcomeCompleted = topics?.find(
            (l) => l.id === 2,
        )?.is_completed;

        setWelcomeCompleted(isWelcomeCompleted);
    }, [courseId, navigate, courses]);
    // Handle welcome completion
    const handleWelcomeComplete = () => {
        setWelcomeCompleted(true);
        isNextButtonDisabled();
    };
    const isNextButtonDisabled = () => {
        // If is_items_controls is 0, always return false

        if (course?.is_items_controls === 0) {
            return false;
        }
        // If is_items_controls is 1
        if (course?.is_items_controls === 1) {
            // If webinar_audit_control is 0, return false
            if (userSettings?.webinar_audit_control === 0) {
                return false;
            }
            // If webinar_audit_control is 1, return !welcomeCompleted
            if (
                userSettings?.webinar_audit_control === 1 ||
                userSettings?.webinar_audit_control === undefined
            ) {
                return !welcomeCompleted;
            }
        }
    };
    // Navigation handlers
    const handleBackToCourse = () => {
        navigate(`/webinars/lms/${courseId}`);
    };

    const handleBackToWebinars = () => {
        navigate('/webinars/lms');
    };

    const handleNextStep = () => {
        // Allow navigation if video is completed OR if course controls are disabled
        if (
            welcomeCompleted ||
            !course?.is_items_controls ||
            userSettings?.webinar_audit_control
        ) {
            navigate(`/webinars/lms/${courseId}/quiz`);
        }
        handleVideoComplete();
    };

    // Handle video completion
    const handleVideoComplete = () => {
        if (!welcomeCompleted) {
            updateCourseProgress(course.id, 2, 'video');
        }
        handleWelcomeComplete();
    };

    // Get tooltip text for next button
    const getNextButtonTooltip = () => {
        return welcomeCompleted ||
            !course?.is_items_controls ||
            !userSettings.webinar_audit_control
            ? 'Next'
            : 'Please watch the video completely before proceeding';
    };

    if (!course) {
        return (
            <Container sx={{ py: 4 }}>
                <Typography>Loading welcome page...</Typography>
            </Container>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <SEO pageTitle={`Welcome to ${course.name} | Toolkit.law`} />

            <ReturnBack pageTitle={course.name} />
            <Box sx={{ p: 3 }} />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Welcome content layout - same as lesson page */}
                <Grid container spacing={3}>
                    {/* Welcome video/image column */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={2}
                            sx={{
                                overflow: 'hidden',
                                borderRadius: 2,
                                height: '100%',
                                border: '1px solid #ccc',
                            }}
                        >
                            <VideoPlayer
                                key={`welcome-${courseId}`} // Unique key for welcome video
                                videoUrl="https://www.youtube.com/watch?v=chjeMJpT-88"
                                onComplete={handleVideoComplete} // Handle video completion
                                isCompleted={welcomeCompleted}
                                courseId={course?.id}
                                videoControls={
                                    userSettings?.webinar_audit_control === 1 ||
                                    course?.is_items_controls === 1
                                } // Default to true for welcome
                                courseItemId="welcome" // Static ID for welcome
                            />
                        </Paper>
                    </Grid>

                    {/* Description column */}
                    <Grid item xs={12} md={4}>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflowY: 'auto',
                                border: '1px solid #ccc',
                            }}
                        >
                            <Typography
                                variant="h5"
                                component="h2"
                                gutterBottom
                            >
                                Welcome to {course.name}
                            </Typography>
                            <Box
                                sx={{
                                    overflowY: 'auto',
                                    flex: 1,
                                    pr: 1,
                                    mt: 1,
                                }}
                            >
                                <Typography variant="body1">
                                    Complete this entire slide to advance to
                                    next. Follow along with corresponding
                                    webinar materials from our{' '}
                                    <Link
                                        href={`${config.siteUrl}/webinars/helpsheets`}
                                        target="_blank"
                                        rel="noopener"
                                        sx={{
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                color: 'primary.main', // keep it the same
                                            },
                                        }}
                                    >
                                        Helpsheets
                                    </Link>{' '}
                                    page.
                                </Typography>
                            </Box>

                            {/* Navigation section */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                }}
                            >
                                <Tooltip title="Previous">
                                    <IconButton
                                        onClick={handleBackToCourse}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'black',
                                            borderRadius: '10px',
                                        }}
                                    >
                                        <ArrowBack fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Typography
                                    sx={{ mx: 2, fontWeight: 'medium' }}
                                >
                                    Slide 02/
                                    {(topics.length + 1)
                                        .toString()
                                        .padStart(2, '0')}
                                </Typography>
                                <Tooltip title={getNextButtonTooltip()}>
                                    <span>
                                        <IconButton
                                            onClick={handleNextStep}
                                            disabled={isNextButtonDisabled()}
                                            size="small"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'black',
                                                borderRadius: '10px',
                                            }}
                                        >
                                            <ArrowForward fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>

                            {/* Complete button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleBackToWebinars}
                                >
                                    Webinars
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleBackToCourse}
                                >
                                    Slides
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

export default WelcomePage;
